import type { ComponentProps } from 'react'
import { cn } from '../lib/cn'

type ButtonStyleProps = {
  appearance?: 'solid' | 'outline' | 'ghost'
  tone?: 'accent' | 'neutral' | 'success' | 'danger'
  size?: 'sm' | 'md' | 'icon'
  className?: string
}
const colors = {
  accent: {
    solid: 'bg-action text-action-foreground hover:bg-action-hover disabled:hover:bg-action',
    outline: 'border-action/40 text-action hover:bg-accent disabled:hover:bg-transparent',
    ghost: 'text-action hover:bg-accent disabled:hover:bg-transparent',
  },
  neutral: {
    solid: 'bg-foreground text-background hover:bg-foreground/85 disabled:hover:bg-foreground',
    outline: 'border-border bg-card text-foreground hover:bg-muted disabled:hover:bg-card',
    ghost:
      'text-muted-foreground hover:bg-muted hover:text-foreground disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
  },
  success: {
    solid:
      'bg-success-foreground text-background hover:bg-success-foreground/90 disabled:hover:bg-success-foreground',
    outline:
      'border-success-foreground/40 text-success-foreground hover:bg-success disabled:hover:bg-transparent',
    ghost: 'text-success-foreground hover:bg-success disabled:hover:bg-transparent',
  },
  danger: {
    solid:
      'bg-danger-foreground text-white dark:text-background hover:bg-danger-foreground/90 disabled:hover:bg-danger-foreground',
    outline:
      'border-danger-foreground/40 text-danger-foreground hover:bg-danger disabled:hover:bg-transparent',
    ghost: 'text-danger-foreground hover:bg-danger disabled:hover:bg-transparent',
  },
}
export function buttonStyles({
  appearance = 'solid',
  tone = 'accent',
  size = 'md',
  className,
}: ButtonStyleProps = {}) {
  return cn(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-transparent text-sm font-semibold transition-colors touch-manipulation focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-pressed:bg-accent aria-pressed:text-accent-foreground [&>svg]:shrink-0',
    colors[tone][appearance],
    size === 'icon'
      ? 'size-11 p-2.5'
      : size === 'sm'
        ? 'min-h-9 px-4 py-1.5 text-xs'
        : 'min-h-11 px-5 py-2',
    className,
  )
}
export function Button({
  className,
  disabled,
  pending = false,
  type = 'button',
  appearance,
  tone,
  size,
  'aria-busy': busy,
  ...props
}: ComponentProps<'button'> & ButtonStyleProps & { pending?: boolean }) {
  return (
    <button
      {...props}
      data-slot="button"
      type={type}
      disabled={disabled || pending}
      aria-busy={pending || busy}
      className={buttonStyles({ appearance, tone, size, className })}
    />
  )
}
